import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { login } from "../../api/auth";
import {
  prepareFeishuAccess,
  prepareFeishuLogin,
  feishuAccess,
  bindFeishu,
  acceptFeishuLogin,
  consumeFeishuLoginStarted,
  consumeExistingFeishuAccount,
  markFeishuLoginStarted,
  rememberExistingFeishuAccount,
  trustedLoginRedirect,
  type FeishuLoginResult,
} from "../../api/hub/identity";
import { isLoggedIn } from "../../api/session";
import { loginDestination, rememberReturn } from "../../api/loginReturn";
import { Loader2 } from "lucide-react";
import { Pill } from "../../ui";
import { LoginStage, type LoginStageHandle } from "./LoginStage";
import "./login.css";


export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const state = (location.state ?? {}) as { from?: string; expired?: boolean };
  const attempted = useRef(false);
  const bindingIntent = useRef<"auto" | "existing">("auto");
  const preparingRedirect = useRef(false);
  const redirectCancelled = useRef(false);
  const stage = useRef<LoginStageHandle>(null);
  // 打字时每按一下，背景从输入框荡出一圈涟漪
  const ripple = (e: { currentTarget: HTMLElement }) => stage.current?.pulseFrom(e.currentTarget);
  const [ticket, setTicket] = useState("");
  const [bindExisting, setBindExisting] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    state.expired ? "登录已过期，请重新登录" : null,
  );

  async function createForFeishu(bindingTicket: string) {
    setBusy(true);
    setError(null);
    setAutoFailed(false);
    try {
      finish(await bindFeishu(bindingTicket));
    } catch (e) {
      setError(e instanceof Error ? e.message : "自动开通失败，请重试");
      setAutoFailed(true);
      setBusy(false);
    }
  }
  function finish(r: FeishuLoginResult) {
    if (r.bindingRequired && r.ticket) {
      setTicket(r.ticket);
      if (bindingIntent.current === "existing") {
        setBindExisting(true);
        setBusy(false);
      } else {
        setBindExisting(false);
        void createForFeishu(r.ticket);
      }
      return;
    }
    if (acceptFeishuLogin(r)) {
      qc.clear();
      navigate(loginDestination(state.from), {
        replace: true,
      });
    }
  }
  useEffect(() => {
    const hash = new URLSearchParams(location.hash.slice(1));
    const callbackError = hash.get("hub_error");
    if (callbackError) {
      attempted.current = true;
      setError(callbackError.slice(0, 300));
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    const result = hash.get("hub_result");
    if (result && !attempted.current) {
      attempted.current = true;
      window.history.replaceState(null, "", window.location.pathname);
      if (!consumeFeishuLoginStarted()) {
        consumeExistingFeishuAccount();
        setError("登录结果无法确认，请在本页重新点「飞书登录」");
        return;
      }
      bindingIntent.current = consumeExistingFeishuAccount() ? "existing" : "auto";
      try {
        const text = atob(result.replace(/-/g, "+").replace(/_/g, "/"));
        finish(
          JSON.parse(
            new TextDecoder().decode(
              Uint8Array.from(text, (c) => c.charCodeAt(0)),
            ),
          ),
        );
      } catch (e) {
        setError((e as Error).message);
      }
      return;
    }
    const tt = (
      window as unknown as {
        tt?: {
          requestAccess: (o: {
            appID: string;
            scopeList: string[];
            success: (r: { code: string }) => void;
            fail: () => void;
          }) => void;
        };
      }
    ).tt;
    if (!tt || attempted.current || isLoggedIn()) return;
    attempted.current = true;
    setBusy(true);
    void prepareFeishuAccess()
      .then((p) =>
        tt.requestAccess({
          appID: p.appId,
          scopeList: p.scopeList,
          success: (r) => {
            void feishuAccess(r.code, p.state)
              .then(finish)
              .catch((e) => {
                setError(e.message);
                setBusy(false);
              });
          },
          fail: () => {
            setError("飞书免登未完成，可选择下面的登录方式");
            setBusy(false);
          },
        }),
      )
      .catch((e) => {
        setError(e.message);
        setBusy(false);
      });
  }, []);
  const feishuLogin = async (intent: "auto" | "existing") => {
    if (busy) {
      if (intent === "existing") {
        if (preparingRedirect.current) {
          try {
            rememberExistingFeishuAccount();
          } catch (e) {
            redirectCancelled.current = true;
            setError((e as Error).message);
            return;
          }
        }
        bindingIntent.current = "existing";
        setBindExisting(true);
      }
      return;
    }
    bindingIntent.current = intent;
    setBindExisting(intent === "existing");
    redirectCancelled.current = false;
    preparingRedirect.current = true;
    setBusy(true);
    setError(null);
    try {
      if (intent === "existing") rememberExistingFeishuAccount();
      else consumeExistingFeishuAccount();
      const r = await prepareFeishuLogin();
      if (redirectCancelled.current) return;
      const target = trustedLoginRedirect(r.authorizationUrl);
      markFeishuLoginStarted();
      rememberReturn(state.from);
      window.location.assign(target);
    } catch (e) {
      consumeExistingFeishuAccount();
      setError((e as Error).message);
      setBusy(false);
    } finally {
      preparingRedirect.current = false;
      if (redirectCancelled.current) setBusy(false);
    }
  };
  if (isLoggedIn() && !busy) return <Navigate to={loginDestination(state.from, false)} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return setError("请填写邮箱和密码");
    setBusy(true);
    setError(null);
    try {
      if (ticket) {
        finish(await bindFeishu(ticket, email, password));
        return;
      }
      await login(email, password);
      qc.clear();
      navigate(loginDestination(state.from), {
        replace: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "没能登录");
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <LoginStage ref={stage} />
      <div className="login-side">
      <form className="login-box" onSubmit={submit} noValidate>
        <h1>登录灵智原生</h1>
        {!ticket && (
          <>
            <Pill
              type="button"
              variant="primary"
              size="lg"
              disabled={busy}
              onClick={() => void feishuLogin("auto")}
            >
              飞书登录
            </Pill>
            <Pill
              type="button"
              size="lg"
              onClick={() => void feishuLogin("existing")}
            >
              已有知识库账号？先绑定再登录
            </Pill>
            <p className="login-or">或用知识库账号</p>
          </>
        )}
        {ticket && bindExisting && (
          <p className="lead">
            首次飞书登录：输入已有知识库账号和密码，验证后绑定。
          </p>
        )}
        {ticket && !bindExisting && (
          <p className="lead">
            {autoFailed ? "自动开通未完成，可重试或绑定已有账号。" : "正在为飞书身份开通知识库账号…"}
          </p>
        )}
        {(!ticket || bindExisting) && (
          <>
            <label className="sr-only" htmlFor="login-email">邮箱</label>
            <input
              id="login-email"
              className="login-field"
              type="email"
              autoComplete="username"
              placeholder="邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={ripple}
              autoFocus
            />
            <label className="sr-only" htmlFor="login-password">密码</label>
            <input
              id="login-password"
              className="login-field"
              type="password"
              autoComplete="current-password"
              placeholder="密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={ripple}
            />
          </>
        )}
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        {(!ticket || bindExisting) && (
          <Pill
            type="submit"
            variant={ticket ? "primary" : "soft"}
            size="lg"
            disabled={busy}
            aria-busy={busy}
            icon={busy ? <Loader2 className="spin" /> : undefined}
            style={{ width: "100%", marginTop: 6 }}
          >
            {ticket ? "绑定并登录" : "账号登录"}
          </Pill>
        )}
        {ticket && !bindExisting && autoFailed && (
          <Pill
            type="button"
            disabled={busy}
            onClick={() => void createForFeishu(ticket)}
          >
            重试自动开通
          </Pill>
        )}
        {ticket && !bindExisting && autoFailed && (
          <Pill
            type="button"
            disabled={busy}
            onClick={() => {
              bindingIntent.current = "existing";
              setBindExisting(true);
              setError(null);
            }}
          >
            改为绑定已有知识库账号
          </Pill>
        )}
        {ticket && !busy && (
          <Pill type="button" onClick={() => void feishuLogin(bindingIntent.current)}>
            重新飞书登录
          </Pill>
        )}
      </form>
      </div>
    </main>
  );
}
